import React from 'react';
import styled from 'styled-components';
import AddressLineField from '../../../app/common/fields/AddressLineField';
import CityField from '../../../app/common/fields/CityField';
import CompanyNameField from '../../../app/common/fields/CompanyNameField';
import FirstNameField from '../../../app/common/fields/FirstNameField';
import LastNameField from '../../../app/common/fields/LastNameField';
import PhoneNumberField from '../../../app/common/fields/phoneNumber/PhoneNumberField';
import PostcodeField from '../../../app/common/fields/postalcode/PostalCodeField';
import StateField from '../../../app/common/fields/state/StateField';
import { IAddress, IAddressFieldConfig } from '../address/address.interface';
import { CountryFieldContainer } from '../../../app/common/fields/country/CountryFieldContainer';
import {
    TwoColFormWrapper,
    TwoColFormLeftElement,
    TwoColFormRightElement,
    SingleFormElementWrapper,
} from '../../../app/common/components/FormLayoutStyles';
import { DEFAULT_ADDRESS_FIELD_CONFIG } from '../addressFormConfig';

const FieldsWrapper = styled.div``;

interface IAddressFormField {
    autoFocus?: boolean;
    values: Partial<IAddress>;
    isEditing?: boolean;
    addressFieldConfig?: IAddressFieldConfig[][];
}

const onChangeCountry = (
    setFieldValue: (field: string, value: any) => void,
    countryCode: string,
) => {
    if (countryCode === 'TW') {
        setFieldValue('city', '');
        setFieldValue('postalCode', '');
    }
    setFieldValue('cityCode', '');
};

export const AddressFormFields = ({
    autoFocus = false,
    values,
    addressFieldConfig,
}: IAddressFormField) => {
    const renderField = (field: IAddressFieldConfig) => {
        switch (field.name) {
            case 'firstName':
                return (
                    <FirstNameField
                        key={field.name}
                        autoFocus={autoFocus}
                        value={values.firstName}
                        required={field.required}
                        validatorRules={field.validatorRules}
                    />
                );
            case 'lastName':
                return (
                    <LastNameField
                        key={field.name}
                        value={values.lastName}
                        required={field.required}
                        validatorRules={field.validatorRules}
                    />
                );
            case 'companyName':
                return (
                    <CompanyNameField
                        key={field.name}
                        value={values.companyName || ''}
                        required={field.required}
                        validatorRules={field.validatorRules}
                    />
                );
            case 'country':
                return (
                    <CountryFieldContainer
                        key={field.name}
                        countryCodeFieldId="countryCode"
                        countryCodeFieldValue={values.countryCode}
                        required={field.required}
                        onChange={onChangeCountry}
                        validatorRules={field.validatorRules}
                    />
                );
            case 'state':
                return (
                    <StateField
                        key={field.name}
                        countryCode={values.countryCode}
                        stateCodeFieldId="stateCode"
                        stateCodeFieldValue={values.stateCode}
                        stateNameFieldId="state"
                        stateNameFieldValue={values.state}
                        value={values.state}
                        required={field.required}
                    />
                );
            case 'addressLine1':
                return (
                    <AddressLineField
                        key={field.name}
                        count={1}
                        value={values.addressLine1}
                        required={field.required}
                        validatorRules={field.validatorRules}
                    />
                );
            case 'addressLine2':
                return (
                    <AddressLineField
                        key={field.name}
                        count={2}
                        value={values.addressLine2 || ''}
                        required={field.required}
                        validatorRules={field.validatorRules}
                    />
                );
            case 'city':
                return (
                    <CityField
                        key={field.name}
                        value={values.city}
                        code={values.cityCode}
                        countryCode={values.countryCode}
                        state={values.state}
                        required={field.required}
                        validatorRules={field.validatorRules}
                    />
                );
            case 'postcode':
                return (
                    <PostcodeField
                        key={field.name}
                        value={values.postalCode}
                        required={field.required}
                        disabled={values.countryCode === 'TW'}
                    />
                );
            case 'phoneNumber':
                return (
                    <PhoneNumberField
                        key={field.name}
                        defaultCountry={values.countryCode ? values.countryCode : 'US'}
                        value={values.phoneNumber || ''}
                        required={field.required}
                        label={'phone_number'}
                        tooltipText="This phone number is used to solve delivery issues"
                        validatorRules={field.validatorRules}
                    />
                );
            default:
                return null;
        }
    };
    const renderConfig =
        addressFieldConfig && addressFieldConfig.length > 0
            ? addressFieldConfig
            : DEFAULT_ADDRESS_FIELD_CONFIG;
    return (
        <FieldsWrapper>
            {renderConfig.map((itemConfigs, index) => {
                if (itemConfigs.length === 2) {
                    return (
                        <TwoColFormWrapper key={index}>
                            <TwoColFormLeftElement>
                                {renderField(itemConfigs[0])}
                            </TwoColFormLeftElement>
                            <TwoColFormRightElement>
                                {renderField(itemConfigs[1])}
                            </TwoColFormRightElement>
                        </TwoColFormWrapper>
                    );
                } else if (itemConfigs.length === 1) {
                    return (
                        <SingleFormElementWrapper key={index}>
                            {renderField(itemConfigs[0])}
                        </SingleFormElementWrapper>
                    );
                } else {
                    return null;
                }
            })}
        </FieldsWrapper>
    );
};
